import React, { useMemo } from 'react';
import { Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import TableIndex from '../table/TableIndex';
import type { TableService } from '../table/TableIndex';

interface ProductLike {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface ProductServiceLike {
  getSerialNumbers: (productId: string, params: Record<string, unknown>) => Promise<{ data: unknown[]; has_more: boolean }>;
  [key: string]: unknown;
}

interface SerialNumberRow {
  id: string;
  serial_number?: string;
  order_id?: string;
  order_name?: string;
  shipped_at?: string;
  created_at?: string;
  [key: string]: unknown;
}

interface ProductSerialNumbersDrawerProps {
  open: boolean;
  onClose: () => void;
  product: ProductLike | null;
  // serialNumberService memo 內已 guard `if (!productService) return null`，
  // 故 Props 維持可選以對齊 runtime。
  productService?: ProductServiceLike;
  role?: 'admin' | 'operator';
}

const ProductSerialNumbersDrawer: React.FC<ProductSerialNumbersDrawerProps> = ({
  open,
  onClose,
  product,
  productService,
  role = 'operator'
}) => {
  const { t } = useTranslation();

  const columns = useMemo(() => [
    {
      name: t('shared.products.serial_numbers.serial_number'),
      selector: (row: SerialNumberRow) => row.serial_number,
    },
    {
      name: t('shared.products.serial_numbers.order_name'),
      selector: (row: SerialNumberRow) => row.order_name,
      cell: (row: SerialNumberRow) => (
        <Link to={`/orders/${row.order_id}`}>
          {row.order_name}
        </Link>
      ),
    },
    {
      name: t('shared.products.serial_numbers.shipped_at'),
      selector: (row: SerialNumberRow) => row.shipped_at,
    },
    {
      name: t('common.created_at'),
      selector: (row: SerialNumberRow) => row.created_at,
    },
  ], [t]);

  // Create a service wrapper for serial numbers
  const serialNumberService = useMemo<TableService | null>(() => {
    if (!product || !productService) return null;
    return {
      getRecords: (params: Record<string, unknown>) => productService.getSerialNumbers(product.id, params),
    };
  }, [product, productService]);

  if (!product) return null;

  return (
    <Drawer
      title={`${t('shared.products.serial_numbers.title')} - ${product.name}`}
      placement="right"
      width={1000}
      onClose={onClose}
      open={open}
    >
      {open && serialNumberService && (
        <TableIndex
          columns={columns}
          service={serialNumberService}
          tableId="product-shipping-serial-numbers"
          searchable={true}
          columnsEditable={false}
        />
      )}
    </Drawer>
  );
};

export default ProductSerialNumbersDrawer;
